<template>
  <div class="step-2-container">
    <v-row>
      <v-col cols="12">
        <mf-info-card variant="warning" icon="warning" :v-html="step2Warning" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <config-selected-model
          :display-title-error="$v.highlight.displayTitle.$error"
          :display-title-error-message="errorDisplayTitle"
          :icon-image-error="$v.highlight.iconImage.$error"
          :banner-image-error="$v.highlight.bannerImage.$error"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <config-offers-list :display-title-error="$v.highlight.displayTitle.$error" :display-title-error-message="errorDisplayTitle" />
      </v-col>
    </v-row>

    <mf-action-buttons :primary-button="primaryButton" :secondary-button="secondaryButton" :extra-button="backButton" :cancel-button="cancelButton" />
  </div>
</template>

<script>
import { highlightOffer } from '../../../../mixins/highlight-offer'
import { notOnlySpace } from '@/helpers/validators.js'
import { minLength, maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'HighlightStep2',
  components: {
    ConfigSelectedModel: () => import('./ConfigSelectedModel.vue'),
    ConfigOffersList: () => import('./ConfigOffersList.vue'),
  },
  mixins: [highlightOffer],
  data: () => ({
    loading: {
      saveDraft: false,
      saveActive: false,
    },
  }),
  computed: {
    highlight: {
      set(value) {
        this.setHighlightObject(value)
      },
      get() {
        return this.highlightOffer
      },
    },
    anyLoading() {
      return Object.entries(this.loading).some(([key, value]) => value)
    },
    step2Warning() {
      return '<span  class="warning-message"><b>Atenção: </b> Esta pré-visualização é uma representação aproximada e não reflete exatamente como o destaque será exibido no aplicativo.</span>'
    },
    activeHighlight() {
      return this.highlightStatus === 'ACTIVE'
    },
    finishedHighlight() {
      return this.highlightStatus === 'FINISHED'
    },
    draftHighlight() {
      return this.highlightStatus === 'DRAFT'
    },
    scheduledHighlight() {
      return this.highlightStatus === 'SCHEDULED'
    },
    primaryButton() {
      return {
        text: this.draftHighlight ? 'Ativar destaque' : 'Salvar e sair',
        action: this.saveActive,
        isVisible: true,
        isDisabled: this.loading.saveDraft || this.finishedHighlight,
        isLoading: this.loading.saveActive,
      }
    },
    secondaryButton() {
      return {
        text: 'Salvar rascunho',
        action: this.saveDraft,
        isVisible: this.draftHighlight,
        isDisabled: this.anyLoading && !this.loading.saveDraft,
        isLoading: this.loading.saveDraft,
      }
    },
    backButton() {
      return {
        text: 'Voltar',
        action: this.goBack,
        isVisible: true,
        isDisabled: this.loading.saveDraft,
        isLoading: false,
      }
    },
    cancelButton() {
      return {
        text: this.finishedHighlight ? 'Fechar' : 'Cancelar',
        action: this.cancel,
        isVisible: true,
        isDisabled: this.anyLoading,
        isLoading: false,
      }
    },
    errorDisplayTitle() {
      if (!this.$v.highlight.displayTitle) return ''
      if (this.$v.highlight.displayTitle.$error) {
        if (!this.$v.highlight.displayTitle.required) return 'Dê um título para esse destaque.'
        else if (!this.$v.highlight.displayTitle.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.highlight.displayTitle.maxLength) return 'O título possui mais de 25 caracteres.'
        else if (!this.$v.highlight.displayTitle.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    products() {
      return this.highlightOffer.products.map((product) => {
        return {
          sku: product.sku,
          showHome: product.hasOwnProperty('showHome') ? product.showHome : false,
        }
      })
    },
  },
  methods: {
    async cancel() {
      let confirmed = false
      if (!this.finishedHighlight) {
        confirmed = await this.$confirm({
          title: 'Tem certeza?',
          message:
            'Seu destaque ainda não foi salvo e todas as suas alterações recentes serão perdidas ao sair dessa página. Para continuar editando clique em <strong>continuar editando</strong>, para sair sem salvar clique em <strong>descartar alterações</strong>.',
          confirmText: 'Continuar editando',
          cancelText: 'Descartar alterações',
        })
          .then(() => false)
          .catch(() => true)
      } else {
        confirmed = true
      }
      if (confirmed) this.$router.push('/mobile/destaques').catch(() => {})
    },

    async saveActive() {
      this.loading.saveActive = true
      let validateFields = true
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        validateFields = false
        this.scrollWindowToError('.error--text')
        this.scrollWindowToError('.error-message')
      } else if (this.highlightTag.active && !this.tagType) {
        validateFields = false
        this.$snackbar({ message: 'Selecione um tipo de tag.', snackbarColor: 'error' })
      } else if (this.highlightTag.active && ['use-image'].includes(this.tagType) && this.highlightTag.image.length === 0) {
        validateFields = false
        this.$snackbar({ message: 'Ao selecionar o tipo de tag com imagem personalizada, você deve inserir uma imagem.', snackbarColor: 'error' })
      } else if (this.isUsingHeaderImage && !this.highlightBannerImage.length) {
        validateFields = false
        this.$snackbar({ message: 'Ao selecionar a opção de imagem de cabeçalho na listagem, você deve inserir uma imagem.', snackbarColor: 'error' })
      }

      if (validateFields) {
        const variables = {
          ...this.highlightOffer,
          products: this.products,
          productsGroups:
            this.highlightOffer.productsGroups?.map((group) => ({
              product_group_id: group._id || group.product_group_id,
              showHome: group.showHome ?? false,
            })) ?? [],
          draft: false,
        }

        try {
          const validate = await this.upsertHighlight({ input: variables })
          if (validate) {
            this.$snackbar({ message: 'Destaque salvo com sucesso', snackbarColor: 'success' })
            this.$router.push('/mobile/destaques').catch(() => {})
          }
        } catch (error) {
          this.$snackbar({ message: 'Erro ao salvar destaque.', snackbarColor: 'error' })
        }
      }

      this.loading.saveActive = false
    },

    scrollWindowToError(querySelector) {
      this.$nextTick(() => {
        let element = document.querySelector(querySelector)
        if (element) {
          this.$vuetify.goTo(element, { offset: 200 })
          this.$snackbar({ message: 'Preencha todos os campos obrigatórios.', snackbarColor: 'error' })
        } else {
          this.$snackbar({ message: 'Erro ao salvar, por favor, verifique os campos', snackbarColor: 'error' })
          window.scrollTo(0, 0)
        }
      })
    },

    async saveDraft() {
      this.loading.saveDraft = true
      this.setHighlightKeyValue({ payload: true, key: 'draft' })

      const variables = {
        ...this.highlightOffer,
        products: this.products,
        productsGroups:
          this.highlightOffer.productsGroups?.map((group) => ({
            product_group_id: group._id || group.product_group_id,
            showHome: group.showHome ?? false,
          })) ?? [],
      }

      try {
        const validate = await this.upsertHighlight({ input: variables })
        if (validate) {
          this.$snackbar({ message: 'Rascunho salvo com sucesso', snackbarColor: 'success' })
          this.$router.push('/mobile/destaques').catch(() => {})
        }
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao salvar rascunho.', snackbarColor: 'error' })
      }

      this.loading.saveDraft = false
    },

    goBack() {
      this.$emit('change-step', 1)
    },
  },
  validations() {
    return {
      highlight: {
        displayTitle: {
          required: (value) => {
            if (this.isDefaultModel) {
              return value.length > 0
            } else return true
          },
          minLength: minLength(2),
          notOnlySpace,
          maxLength: maxLength(25),
        },
        bannerImage: {
          required: (value) => {
            if (this.isExpandedModel) {
              return value.length > 0
            } else return true
          },
        },
        iconImage: {
          required: (value) => {
            if (this.isDefaultModel) {
              return value.length > 0
            } else return true
          },
        },
      },
    }
  },
}
</script>
